import { useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

const slideVariants = {
  enter: (direction) => ({
    opacity: 0,
    x: direction > 0 ? 40 : -40,
  }),
  center: { opacity: 1, x: 0 },
  exit: (direction) => ({
    opacity: 0,
    x: direction > 0 ? -40 : 40,
  }),
};

const VenueCarousel = ({ images = [] }) => {
  const [[index, direction], setIndex] = useState([0, 0]);
  const total = images.length;

  const paginate = useCallback(
    (dir) => {
      setIndex(([current]) => [(current + dir + total) % total, dir]);
    },
    [total]
  );

  if (total === 0) return null;
  const current = images[index];

  return (
    <div
      className="relative w-full"
      data-testid="venue-carousel"
      aria-roledescription="carousel"
    >
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-[#e7e2d4]">
        <AnimatePresence initial={false} custom={direction} mode="popLayout">
          <motion.img
            key={index}
            src={current.src}
            alt={current.alt}
            className="absolute inset-0 h-full w-full object-cover select-none"
            draggable={false}
            loading="lazy"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              opacity: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
              x: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
            }}
            drag={total > 1 ? "x" : false}
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.15}
            onDragEnd={(_, info) => {
              const threshold = 60;
              if (info.offset.x < -threshold) paginate(1);
              else if (info.offset.x > threshold) paginate(-1);
            }}
          />
        </AnimatePresence>

        {/* Desktop arrows — subtle, only visible on md+ */}
        {total > 1 && (
          <>
            <button
              type="button"
              aria-label="Foto anterior"
              onClick={() => paginate(-1)}
              className="absolute left-3 top-1/2 hidden -translate-y-1/2 items-center justify-center rounded-full bg-[#f4efe3]/85 p-2 text-[#0a0a0a] shadow-sm backdrop-blur-sm transition hover:bg-[#f4efe3] md:flex"
              data-testid="venue-carousel-prev"
            >
              <ChevronLeft className="h-5 w-5" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              aria-label="Foto siguiente"
              onClick={() => paginate(1)}
              className="absolute right-3 top-1/2 hidden -translate-y-1/2 items-center justify-center rounded-full bg-[#f4efe3]/85 p-2 text-[#0a0a0a] shadow-sm backdrop-blur-sm transition hover:bg-[#f4efe3] md:flex"
              data-testid="venue-carousel-next"
            >
              <ChevronRight className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </>
        )}
      </div>

      {/* Dot indicators + counter — sits below, doesn't take much vertical space */}
      {total > 1 && (
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="venue-carousel-dots">
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Ir a la foto ${i + 1}`}
                aria-current={i === index}
                onClick={() => setIndex([i, i > index ? 1 : -1])}
                className="h-[6px] rounded-full transition-all"
                style={{
                  width: i === index ? 22 : 6,
                  backgroundColor: i === index ? "#0a0a0a" : "#c9c3b3",
                }}
                data-testid={`venue-carousel-dot-${i}`}
              />
            ))}
          </div>
          <p className="corner-label text-[#8a8580]">
            {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </p>
        </div>
      )}
    </div>
  );
};

export default VenueCarousel;
